import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent } from '../../../components/ui';
import { useLanguageStore } from '../../../store/languageStore';
import { getUITranslation } from '../i18n/translations';
import { joinRoom as joinRoomAPI } from '../services/api';

export const JoinRoom = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguageStore();
  const t = getUITranslation(language);

  // Get code from URL if available
  const codeFromUrl = searchParams.get('code') || '';

  const [formData, setFormData] = useState({
    roomCode: codeFromUrl,
    userName: '',
    email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const normalizedRoomCode = formData.roomCode.trim().toUpperCase();

      const response = await joinRoomAPI(normalizedRoomCode, {
        userName: formData.userName,
        email: formData.email,
      });

      // Save credentials to sessionStorage
      sessionStorage.setItem('userEmail', formData.email);
      sessionStorage.removeItem('adminToken');
      sessionStorage.setItem('userToken', response.userToken);
      sessionStorage.setItem('roomId', normalizedRoomCode);
      // Decode token (format: base64(roomId:userId:isAdmin))
      try {
        const decodedToken = atob(response.userToken);
        const tokenParts = decodedToken.split(':');
        const userId = tokenParts[1] || `user-${Date.now()}`;
        const isAdmin = tokenParts[2] === 'true';
        sessionStorage.setItem('userId', userId);
        if (isAdmin) {
          sessionStorage.setItem('adminToken', response.userToken);
        }
      } catch (e) {
        console.error('Failed to decode user token:', e);
        const fallbackUserId = `user-${Date.now()}`;
        sessionStorage.setItem('userId', fallbackUserId);
      }

      // Navigate to room page with the room ID
      navigate(`/apps/delegation-poker/room/${normalizedRoomCode}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.name === 'roomCode' ? e.target.value.toUpperCase() : e.target.value,
    });
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <section className="mb-10 text-center">
          <div className="mb-4 text-5xl">🚪</div>
          <h1 className="mb-3 text-4xl font-bold md:text-5xl">
            <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
              {t.joinRoom.title}
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-base text-muted-foreground md:text-lg">
            {t.joinRoom.subtitle}
          </p>
        </section>

        <Card className="relative overflow-hidden border-2 bg-gradient-to-br from-secondary/10 via-transparent to-primary/10">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-transparent" />
          <CardContent className="relative p-8 md:p-10">
            {error && (
              <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="roomCode" className="mb-2 block text-sm font-semibold">
                  {t.joinRoom.roomCode}
                </label>
                <input
                  type="text"
                  id="roomCode"
                  name="roomCode"
                  value={formData.roomCode}
                  onChange={handleChange}
                  placeholder={t.joinRoom.roomCodePlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 font-mono uppercase tracking-[0.2em] transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div>
                <label htmlFor="userName" className="mb-2 block text-sm font-semibold">
                  {t.joinRoom.playerName}
                </label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  placeholder={t.joinRoom.playerNamePlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold">
                  {t.joinRoom.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.joinRoom.emailPlaceholder}
                  required
                  disabled={loading}
                  className="h-11 w-full rounded-lg border border-border/80 bg-background/80 px-4 transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => navigate('/apps/delegation-poker')}
                  disabled={loading}
                >
                  {t.joinRoom.cancel}
                </Button>
                <Button type="submit" size="lg" className="flex-1" disabled={loading}>
                  {loading ? 'Joining...' : t.joinRoom.join}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
